import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { V2Layout, NeonDivider, HeroDivider } from './V2Shared';
import { NotaryBookingWidget } from '../NotaryBooking';
import SEO, { buildBreadcrumb, SITE_URL } from '../components/SEO';

const NAPPS_BADGE =
  'https://customer-assets.emergentagent.com/job_legal-site-update/artifacts/lxkv94qi_NAPPS4.png';

/* ============== V2 ABOUT — fully custom editorial ============== */
function V2AboutHero() {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.2}px, 0) scale(${1 + Math.min(y, 600) * 0.0002})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section className="v2-about-hero" data-testid="v2-about-hero">
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: 'url(/lubbock-about.jpg)' }}
      />
      <div className="v2-hero-grain" />
      <div className="v2-about-hero-body">
        <div className="v2-mono-tag">FOUNDED AUGUST 2016</div>
        <h1 className="v2-about-title">
          Eighteen years <em>in the system.</em>
          <br />
          <span className="v2-about-title-line2">Still answering <em>the phone.</em></span>
        </h1>
        <p className="v2-about-sub">
          Built on courthouse-level precision, modern technology, and the kind of client care that actually picks up.
        </p>
      </div>
    </section>
  );
}

function ApproachSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">OUR APPROACH</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Finding a good process server <em>is harder than it should be.</em>
            </h2>
            <p>
              Lousy attitudes. Poor communication. Negligence. We've heard the complaints from every firm we've ever picked up the phone for. WesTex Legal handles process service for Lubbock County, the surrounding region, and statewide — without any of that.
            </p>
            <p>
              Our team is trained to locate any individual using advanced skip-trace techniques. Backed by an extensive nationwide network of professionals, we serve them wherever they go.
            </p>
            <div className="v2-about-pullquote">
              <span className="v2-about-pullquote-mark">"</span>
              The satisfaction of every client is our <em>number one priority.</em>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TechSection() {
  const features = [
    { label: 'Real-time Tracking', desc: 'Every attempt logged and visible the moment it happens.' },
    { label: 'Automatic Updates', desc: 'No more checking in — status changes ping your inbox.' },
    { label: 'Detailed Reports', desc: 'Notes, photos, and timestamps on every attempt.' },
    { label: 'Secure Portal', desc: '24/7 access to your full case history and affidavits.' },
  ];
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">TECHNOLOGY</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Built on <em>ServeManager.</em>
            </h2>
            <p>
              Our case management runs on ServeManager — the industry standard. Clients track every job in real time, see every attempt, and watch the status update without ever picking up the phone.
            </p>
            <div className="v2-about-grid">
              {features.map((f) => (
                <div key={f.label} className="v2-about-grid-item">
                  <div className="v2-about-grid-label">{f.label}</div>
                  <div className="v2-about-grid-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NappsSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">AFFILIATIONS</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Proud <em>NAPPS member.</em>
            </h2>
            <p>
              We are a proud member of the National Association of Professional Process Servers. NAPPS holds applicants to a high bar — and a higher standard for everyone who carries the badge.
            </p>
            <div className="v2-about-napps">
              <img src={NAPPS_BADGE} alt="NAPPS Member" className="v2-about-napps-badge" />
              <div className="v2-about-napps-text">
                <div className="v2-mono-tag" style={{ marginBottom: 6 }}>VERIFIED MEMBER</div>
                <div className="v2-about-napps-name">National Association of Professional Process Servers</div>
                <a
                  href="https://napps.org/ProcessServers.aspx?id=10165"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="v2-about-link"
                >
                  View NAPPS profile →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FounderSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-founder">
          <div className="v2-about-founder-photo">
            <img src="/ryan-photo.jpg" alt="Ryan Crumley — Founder, WesTex Legal" />
            <div className="v2-about-founder-tag">FOUNDER</div>
          </div>
          <div className="v2-about-founder-body">
            <div className="v2-mono-tag">THE TEAM</div>
            <h2 className="v2-about-h2">
              Ryan Crumley.<br />
              <em>Eighteen years inside the system.</em>
            </h2>
            <p>
              Ryan started at the Lubbock County District Clerk's Office, where he was the main point of contact for issuing citations. He helped implement the e-filing system Lubbock County still runs today.
            </p>
            <p>
              After the District Clerks, he went on to work for a personal-injury firm to deepen his understanding of the field — then in August 2016 he founded WesTex Legal, and has been all-in ever since, serving clients across Texas and New Mexico full-time.
            </p>
            <div className="v2-about-creds">
              <div className="v2-about-cred"><span>—</span> 18+ years legal industry experience</div>
              <div className="v2-about-cred"><span>—</span> Established August 2016</div>
              <div className="v2-about-cred"><span>—</span> E-filing system implementation, Lubbock County</div>
              <div className="v2-about-cred"><span>—</span> NAPPS Professional Member</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return null;
}

export function V2About() {
  return (
    <V2Layout>
      <SEO
        title="About WesTex Legal — Founded 2016 by Ryan Crumley | Lubbock TX"
        description="WesTex Legal is a JBCC-certified, NAPPS-member process serving and mobile notary firm founded in 2016 by Ryan Crumley. Serving Texas, New Mexico and statewide via vetted network."
        path="/about"
        image={`${SITE_URL}/lubbock-about.jpg`}
        schema={buildBreadcrumb([
          { name: 'Home', path: '/' },
          { name: 'About', path: '/about' },
        ])}
      />
      <V2AboutHero />
      <HeroDivider />
      <ApproachSection />
      <NeonDivider delay={0.5} />
      <TechSection />
      <NeonDivider delay={1.0} />
      <NappsSection />
      <NeonDivider delay={1.5} />
      <FounderSection />
    </V2Layout>
  );
}

/* ============== V2 SERVICES — fully custom editorial ============== */
function V2ServicesHero() {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.2}px, 0) scale(${1 + Math.min(y, 600) * 0.0002})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section className="v2-about-hero" data-testid="v2-services-hero">
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: 'url(/lubbock-services.jpg)' }}
      />
      <div className="v2-hero-grain" />
      <div className="v2-about-hero-body">
        <div className="v2-mono-tag">SERVICES OFFERED</div>
        <h1 className="v2-about-title">
          Every service.<br />
          <span className="v2-about-title-line2"><em>Under one roof.</em></span>
        </h1>
        <p className="v2-about-sub">
          Process serving in Lubbock, Texas, plus skip tracing, mobile notary, e-filing, and background checks — handled with real-time visibility at every step.
        </p>
      </div>
    </section>
  );
}

const CORE_SERVICES = [
  { num: '01', title: 'Routine Service', desc: 'Up to 4 attempts made on every job — methodical, documented, and transparent.' },
  { num: '02', title: 'Rush Service', desc: 'Up to 4 attempts made on an expedited timeline when the calendar is unforgiving.' },
  { num: '03', title: 'Background Checks', desc: 'Criminal records, evictions, foreclosures, bankruptcies, liens, judgments, current and prior properties, corporate filings, professional licenses — and more.' },
  { num: '04', title: 'Document Retrieval', desc: 'Courier service for retrieving documents at the courthouse. Surrounding counties available with a small upgrade fee per county.' },
  { num: '05', title: 'Skip Tracing', desc: 'Multi-database tracing covering current and past addresses, phone numbers, employers, email addresses, relatives, associates, and possible associates.' },
  { num: '06', title: 'Vehicle Search', desc: 'Determine the current and prior registered owner of a particular vehicle.' },
];

function CoreServicesSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">CORE SERVICES</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              The work, <em>itemized.</em>
            </h2>
            <p>
              Every WesTex Legal engagement is logged, timestamped, and visible in real time. Six core service tracks cover the vast majority of what attorneys, firms, and pro se filers send our way.
            </p>
            <div className="v2-svc-list">
              {CORE_SERVICES.map((s) => (
                <div key={s.num} className="v2-svc-row">
                  <div className="v2-svc-num">{s.num}</div>
                  <div className="v2-svc-body">
                    <div className="v2-svc-title">{s.title}</div>
                    <div className="v2-svc-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const ADDITIONAL_SERVICES = [
  { title: 'Affidavit Preparation', desc: 'Affidavits of Service, Non-Service, Due Diligence (Rule 106), and more — drafted with courthouse-level precision.' },
  { title: 'E-Filing', desc: 'Concierge e-filing service. Send the documents, we handle the rest.' },
  { title: 'Mobile Notary', desc: 'Traveling notary for affirmations, affidavits, and acknowledgments.' },
  { title: 'Nationwide Service', desc: 'Coverage across all 50 states through a vetted network of professional servers.' },
  { title: 'Registered Agent Service', desc: 'Serving CT Corp, CSC, and other major registered agents.' },
  { title: 'Service of Process', desc: 'Residential and business rates, statewide and beyond.' },
  { title: 'Statewide Service', desc: 'All 254 Texas counties and all of New Mexico — no exceptions.' },
  { title: 'Subpoena Service', desc: 'Personal service upon request, executed promptly and documented thoroughly.' },
];

function AdditionalServicesSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">ADDITIONAL SERVICES</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Beyond the basics.
            </h2>
            <p>
              The work that doesn't fit a single line item but still has to get done. We handle it all in-house, on the same docket.
            </p>
            <div className="v2-about-grid">
              {ADDITIONAL_SERVICES.map((s) => (
                <div key={s.title} className="v2-about-grid-item">
                  <div className="v2-about-grid-label">{s.title}</div>
                  <div className="v2-about-grid-desc">{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const WHY_WESTEX = [
  { num: '01', title: 'Real-Time Tracking', desc: 'Every attempt logged the moment it happens. ServeManager-powered visibility, 24/7.' },
  { num: '02', title: 'NAPPS Member', desc: 'Professional standards, ethical practice, and industry-recognized certification.' },
  { num: '03', title: 'Texas + New Mexico', desc: 'Strategic coverage across the entire region with rapid statewide turnaround.' },
  { num: '04', title: 'We Pick Up the Phone', desc: 'Direct-line communication. No phone trees. No "case manager" runaround.' },
];

function WhyWestexSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">WHY WESTEX</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Built for firms that <em>can't afford to chase paperwork.</em>
            </h2>
            <div className="v2-svc-list">
              {WHY_WESTEX.map((s) => (
                <div key={s.num} className="v2-svc-row">
                  <div className="v2-svc-num">{s.num}</div>
                  <div className="v2-svc-body">
                    <div className="v2-svc-title">{s.title}</div>
                    <div className="v2-svc-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function V2Services() {
  return (
    <V2Layout>
      <SEO
        title="Services — Process Serving, Mobile Notary, Skip Tracing | WesTex Legal"
        description="Professional process serving, mobile notary, and skip tracing across Texas, New Mexico, and nationwide. Same-day rush available. Transparent zone-based pricing starting at $75."
        path="/services"
        image={`${SITE_URL}/lubbock-services.jpg`}
        schema={[
          buildBreadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'Process Serving',
            name: 'Process Server in Lubbock, Texas',
            provider: { '@type': 'LegalService', name: 'WesTex Legal', url: SITE_URL },
            areaServed: [
              { '@type': 'City', name: 'Lubbock, TX' },
              { '@type': 'State', name: 'Texas' },
              { '@type': 'State', name: 'New Mexico' },
            ],
            offers: {
              '@type': 'AggregateOffer',
              lowPrice: '75',
              highPrice: '225',
              priceCurrency: 'USD',
            },
          },
        ]}
      />
      <V2ServicesHero />
      <HeroDivider />
      <CoreServicesSection />
      <NeonDivider delay={0.5} />
      <AdditionalServicesSection />
      <NeonDivider delay={1.0} />
      <WhyWestexSection />
    </V2Layout>
  );
}

/* ============== V2 COVERAGE — fully custom editorial ============== */
function V2CoverageHero() {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.2}px, 0) scale(${1 + Math.min(y, 600) * 0.0002})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section className="v2-about-hero" data-testid="v2-coverage-hero">
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: 'url(/lubbock-coverage.jpg)' }}
      />
      <div className="v2-hero-grain" />
      <div className="v2-about-hero-body">
        <div className="v2-mono-tag">COVERAGE & PRICING</div>
        <h1 className="v2-about-title">
          Where we serve.<br />
          <span className="v2-about-title-line2"><em>What it costs.</em></span>
        </h1>
        <p className="v2-about-sub">
          Two regional hubs, statewide reach, and a vetted nationwide network — with transparent mileage-based zone pricing.
        </p>
      </div>
    </section>
  );
}

const COVERAGE_STATS = [
  { num: '254', label: 'Texas counties served' },
  { num: '33', label: 'New Mexico counties' },
  { num: '50', label: 'States via vetted network' },
  { num: '11', label: 'Mileage-based zones' },
];

function CoverageOverviewSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">THE MAP</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Two hubs. <em>Statewide reach.</em>
            </h2>
            <p>
              WesTex Legal operates from regional hubs in Lubbock and Childress, with statewide flat-rate coverage in New Mexico and a network of certified partners covering the rest of the country.
            </p>
            <div className="v2-cov-stats">
              {COVERAGE_STATS.map((s) => (
                <div key={s.label} className="v2-cov-stat">
                  <div className="v2-cov-stat-num">{s.num}</div>
                  <div className="v2-cov-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const LUBBOCK_ZONES = [
  { num: '01', cities: ['Lubbock', 'Wolfforth', 'Slaton', 'Shallowater', 'Smyer', 'Ransom Canyon'], routine: '$75', rush: '$100' },
  { num: '02', cities: ['Levelland', 'Abernathy', 'Tahoka', 'Idalou', 'Brownfield', 'Anton'], routine: '$100', rush: '$130' },
  { num: '03', cities: ['Plainview', 'Petersburg', 'Ralls', 'Post', 'Crosbyton', 'Olton'], routine: '$125', rush: '$160' },
  { num: '04', cities: ['Lamesa', 'Muleshoe', 'Seagraves', 'Lockney', 'Kress', 'Floydada'], routine: '$150', rush: '$195' },
  { num: '05', cities: ['Snyder', 'Seminole', 'Denver City', 'Dimmitt', 'Tulia', 'Nazareth'], routine: '$175', rush: '$225' },
  { num: '06', cities: ['Andrews', 'Friona', 'Bovina', 'Farwell'], routine: '$200', rush: '$255' },
];

const CHILDRESS_ZONES = [
  { num: '01', cities: ['Childress', 'Estelline'], routine: '$95', rush: '$125' },
  { num: '02', cities: ['Quanah', 'Wellington', 'Memphis'], routine: '$125', rush: '$155' },
  { num: '03', cities: ['Vernon', 'Clarendon', 'Shamrock'], routine: '$155', rush: '$185' },
  { num: '04', cities: ['Silverton', 'Benjamin', 'Guthrie'], routine: '$185', rush: '$215' },
  { num: '05', cities: ['Munday', 'Goree', 'Briscoe'], routine: '$215', rush: '$245' },
];

function ZoneList({ zones }) {
  return (
    <div className="v2-zone-list">
      {zones.map((z) => (
        <div key={z.num} className="v2-zone-row">
          <div className="v2-zone-num">Zone {z.num}</div>
          <div className="v2-zone-body">
            <div className="v2-zone-cities">
              {z.cities.map((c, i) => (
                <span key={c}>
                  {c}{i < z.cities.length - 1 ? <span className="v2-zone-dot"> · </span> : null}
                </span>
              ))}
            </div>
          </div>
          <div className="v2-zone-prices">
            <div className="v2-zone-price">
              <div className="v2-zone-price-label">Routine</div>
              <div className="v2-zone-price-val">{z.routine}</div>
            </div>
            <div className="v2-zone-price">
              <div className="v2-zone-price-label">Rush</div>
              <div className="v2-zone-price-val">{z.rush}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LubbockZonesSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">LUBBOCK HUB</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              West Texas, <em>zone by zone.</em>
            </h2>
            <p>
              Six concentric zones radiating from our Lubbock headquarters — pricing scales transparently with mileage.
            </p>
            <ZoneList zones={LUBBOCK_ZONES} />
          </div>
        </div>
      </div>
    </section>
  );
}

function ChildressZonesSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">CHILDRESS HUB</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              North Texas <em>panhandle.</em>
            </h2>
            <p>
              Five zones from our Childress operating base — ideal for the eastern panhandle and Red River corridor.
            </p>
            <ZoneList zones={CHILDRESS_ZONES} />
          </div>
        </div>
      </div>
    </section>
  );
}

function NewMexicoSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">NEW MEXICO</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Statewide. <em>Flat rate.</em>
            </h2>
            <p>
              All 33 counties in New Mexico — Albuquerque, Las Cruces, Santa Fe, Roswell, Hobbs, Carlsbad and everywhere between. One price, no surprises.
            </p>
            <div className="v2-nm-rates">
              <div className="v2-nm-rate">
                <div className="v2-nm-rate-label">Routine</div>
                <div className="v2-nm-rate-val">$125</div>
              </div>
              <div className="v2-nm-rate">
                <div className="v2-nm-rate-label">Rush</div>
                <div className="v2-nm-rate-val">$175</div>
              </div>
            </div>
            <p className="v2-nm-foot">
              Per address · Statewide flat rate · Same-day rush available
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function NetworkSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">NATIONWIDE</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Beyond Texas. <em>Beyond New Mexico.</em>
            </h2>
            <p>
              Need service in Phoenix, Atlanta, or Anchorage? We coordinate with a vetted network of certified process servers across all 50 states. You get one point of contact — we handle the rest.
            </p>
            <div className="v2-net-grid">
              <div className="v2-net-item">
                <div className="v2-net-label">Coverage</div>
                <div className="v2-net-val">All 50 states · 340+ major metros</div>
              </div>
              <div className="v2-net-item">
                <div className="v2-net-label">Pricing</div>
                <div className="v2-net-val">$90 – $175 per address (location-dependent)</div>
              </div>
              <div className="v2-net-item">
                <div className="v2-net-label">Timeline</div>
                <div className="v2-net-val">3 – 5 business days standard</div>
              </div>
              <div className="v2-net-item">
                <div className="v2-net-label">Don't see your city?</div>
                <div className="v2-net-val">Call (806) 401-1749 for a custom quote.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   V2 COVERAGE CALCULATOR — address autocomplete + map
   ========================================================= */
const LUBBOCK_HUB = { lat: 33.5207062, lng: -101.9060155, name: 'Lubbock' };
const CHILDRESS_HUB = { lat: 34.4135811, lng: -100.208968, name: 'Childress' };

// Lubbock zones — 18.5 miles per zone
const LUBBOCK_ZONE_RADII = [
  { max: 18.5,  zone: 1, routine: '$75',  rush: '$100' },
  { max: 37,    zone: 2, routine: '$100', rush: '$130' },
  { max: 55.5,  zone: 3, routine: '$125', rush: '$160' },
  { max: 74,    zone: 4, routine: '$150', rush: '$195' },
  { max: 92.5,  zone: 5, routine: '$175', rush: '$225' },
  { max: 111,   zone: 6, routine: '$200', rush: '$255' },
];
// Childress zones — 20 miles per zone
const CHILDRESS_ZONE_RADII = [
  { max: 20,  zone: 1, routine: '$95',  rush: '$125' },
  { max: 40,  zone: 2, routine: '$125', rush: '$155' },
  { max: 60,  zone: 3, routine: '$155', rush: '$185' },
  { max: 80,  zone: 4, routine: '$185', rush: '$215' },
  { max: 100, zone: 5, routine: '$215', rush: '$245' },
];

// Strict zone bands (no buffer): a service address is priced into the zone its true
// driving distance falls in — the moment it crosses a boundary (even by a tenth of a
// mile) it bumps to the next zone. Lubbock = 18.5 mi/zone, Childress = 20 mi/zone.
const ZONE_GRACE_MI = 0;

// A search landing within this many miles of a covered town's center is treated as
// "in that town" and quoted at the town's established zone. A specific address
// farther out than this falls through to true-mileage pricing (so outlying county
// addresses correctly cost more).
const TOWN_CORE_MI = 3;

// Zone shading — deepest red for closest zones, fading outward
const ZONE_SHADES = ['#dc2626', '#ef4444', '#f87171', '#fca5a5', '#fecaca', '#fee2e2'];

// New Mexico — entire state covered at one flat rate. A consistent marker color
// (distinct from the Texas red zone shades) reflects the single statewide price.
const NM_FLAT = { routine: '$125', rush: '$175' };
const NM_MARKER_COLOR = '#38bdf8';
const NM_COUNTIES = [
  { name: 'Bernalillo', lat: 35.05, lng: -106.67 },
  { name: 'Catron', lat: 33.91, lng: -108.40 },
  { name: 'Chaves', lat: 33.36, lng: -104.47 },
  { name: 'Cibola', lat: 34.91, lng: -107.99 },
  { name: 'Colfax', lat: 36.60, lng: -104.47 },
  { name: 'Curry', lat: 34.57, lng: -103.34 },
  { name: 'De Baca', lat: 34.34, lng: -104.40 },
  { name: 'Doña Ana', lat: 32.35, lng: -106.83 },
  { name: 'Eddy', lat: 32.46, lng: -104.30 },
  { name: 'Grant', lat: 32.73, lng: -108.39 },
  { name: 'Guadalupe', lat: 34.86, lng: -104.78 },
  { name: 'Harding', lat: 35.86, lng: -103.82 },
  { name: 'Hidalgo', lat: 31.91, lng: -108.71 },
  { name: 'Lea', lat: 32.79, lng: -103.43 },
  { name: 'Lincoln', lat: 33.74, lng: -105.46 },
  { name: 'Los Alamos', lat: 35.87, lng: -106.30 },
  { name: 'Luna', lat: 32.18, lng: -107.75 },
  { name: 'McKinley', lat: 35.58, lng: -108.27 },
  { name: 'Mora', lat: 35.99, lng: -104.95 },
  { name: 'Otero', lat: 32.59, lng: -105.81 },
  { name: 'Quay', lat: 35.10, lng: -103.55 },
  { name: 'Rio Arriba', lat: 36.51, lng: -106.69 },
  { name: 'Roosevelt', lat: 34.02, lng: -103.48 },
  { name: 'Sandoval', lat: 35.68, lng: -106.89 },
  { name: 'San Juan', lat: 36.51, lng: -108.32 },
  { name: 'San Miguel', lat: 35.47, lng: -104.81 },
  { name: 'Santa Fe', lat: 35.51, lng: -105.97 },
  { name: 'Sierra', lat: 33.13, lng: -107.19 },
  { name: 'Socorro', lat: 34.01, lng: -106.93 },
  { name: 'Taos', lat: 36.58, lng: -105.66 },
  { name: 'Torrance', lat: 34.64, lng: -105.92 },
  { name: 'Union', lat: 36.48, lng: -103.47 },
  { name: 'Valencia', lat: 34.73, lng: -106.82 },
];

// Covered cities with coordinates + hub/zone assignment
const COVERED_CITIES = [
  // Lubbock Zone 1
  { name: 'Lubbock', lat: 33.5779, lng: -101.8552, hub: 'Lubbock', zone: 1 },
  { name: 'Wolfforth', lat: 33.5048, lng: -102.0090, hub: 'Lubbock', zone: 1 },
  { name: 'Slaton', lat: 33.4373, lng: -101.6429, hub: 'Lubbock', zone: 1 },
  { name: 'Shallowater', lat: 33.6909, lng: -101.9988, hub: 'Lubbock', zone: 1 },
  { name: 'Idalou', lat: 33.6626, lng: -101.7068, hub: 'Lubbock', zone: 2 },
  { name: 'Ransom Canyon', lat: 33.5333, lng: -101.6797, hub: 'Lubbock', zone: 1 },
  { name: 'Smyer', lat: 33.5881, lng: -102.1638, hub: 'Lubbock', zone: 1 },
  { name: 'Buffalo Springs Lake', lat: 33.53584, lng: -101.72245, hub: 'Lubbock', zone: 1 },
  { name: 'New Deal', lat: 33.7334, lng: -101.8296, hub: 'Lubbock', zone: 2 },
  // Lubbock Zone 2
  { name: 'Levelland', lat: 33.5873, lng: -102.3577, hub: 'Lubbock', zone: 2 },
  { name: 'Abernathy', lat: 33.8348, lng: -101.8429, hub: 'Lubbock', zone: 2 },
  { name: 'Tahoka', lat: 33.1668, lng: -101.7929, hub: 'Lubbock', zone: 2 },
  { name: 'Ralls', lat: 33.6884, lng: -101.3851, hub: 'Lubbock', zone: 2 },
  { name: 'Anton', lat: 33.8098, lng: -102.1621, hub: 'Lubbock', zone: 2 },
  { name: 'Ropesville', lat: 33.4132, lng: -102.1544, hub: 'Lubbock', zone: 2 },
  { name: 'Lorenzo', lat: 33.6715, lng: -101.5354, hub: 'Lubbock', zone: 2 },
  { name: 'New Home', lat: 33.3269, lng: -101.9117, hub: 'Lubbock', zone: 1 },
  { name: 'Meadow', lat: 33.3495, lng: -102.2051, hub: 'Lubbock', zone: 2 },
  // Lubbock Zone 3
  { name: 'Petersburg', lat: 33.8720, lng: -101.5968, hub: 'Lubbock', zone: 3 },
  { name: 'Plainview', lat: 34.1848, lng: -101.7068, hub: 'Lubbock', zone: 3 },
  { name: 'Brownfield', lat: 33.1815, lng: -102.2690, hub: 'Lubbock', zone: 2 },
  { name: 'Littlefield', lat: 33.9137, lng: -102.3243, hub: 'Lubbock', zone: 3 },
  { name: 'Post', lat: 33.1901, lng: -101.3790, hub: 'Lubbock', zone: 3 },
  { name: 'Crosbyton', lat: 33.6618, lng: -101.2371, hub: 'Lubbock', zone: 3 },
  { name: 'Olton', lat: 34.1773, lng: -102.1318, hub: 'Lubbock', zone: 3 },
  { name: 'Hale Center', lat: 34.0659, lng: -101.8466, hub: 'Lubbock', zone: 3 },
  { name: 'Sudan', lat: 34.0667, lng: -102.5256, hub: 'Lubbock', zone: 3 },
  { name: 'Whiteface', lat: 33.6001, lng: -102.6096, hub: 'Lubbock', zone: 3 },
  { name: "O'Donnell", lat: 32.9651, lng: -101.8306, hub: 'Lubbock', zone: 3 },
  { name: 'Sundown', lat: 33.4537, lng: -102.4888, hub: 'Lubbock', zone: 3 },
  // Lubbock Zone 4
  { name: 'Floydada', lat: 33.9842, lng: -101.3368, hub: 'Lubbock', zone: 4 },
  { name: 'Lamesa', lat: 32.7376, lng: -101.9507, hub: 'Lubbock', zone: 4 },
  { name: 'Muleshoe', lat: 34.2267, lng: -102.7243, hub: 'Lubbock', zone: 4 },
  { name: 'Seagraves', lat: 32.9426, lng: -102.5635, hub: 'Lubbock', zone: 4 },
  { name: 'Lockney', lat: 34.1267, lng: -101.4429, hub: 'Lubbock', zone: 4 },
  { name: 'Morton', lat: 33.7268, lng: -102.7594, hub: 'Lubbock', zone: 4 },
  { name: 'Plains', lat: 33.1884, lng: -102.8271, hub: 'Lubbock', zone: 4 },
  { name: 'Justiceburg', lat: 33.0387, lng: -101.2104, hub: 'Lubbock', zone: 4 },
  { name: 'Dickens', lat: 33.6201, lng: -100.8364, hub: 'Lubbock', zone: 4 },
  { name: 'Welch', lat: 32.9376, lng: -102.1248, hub: 'Lubbock', zone: 4 },
  { name: 'Earth', lat: 34.2315, lng: -102.4118, hub: 'Lubbock', zone: 4 },
  // Lubbock Zone 5
  { name: 'Spur', lat: 33.4809, lng: -100.8573, hub: 'Lubbock', zone: 5 },
  { name: 'Snyder', lat: 32.7179, lng: -100.9176, hub: 'Lubbock', zone: 5 },
  { name: 'Seminole', lat: 32.7198, lng: -102.6446, hub: 'Lubbock', zone: 5 },
  { name: 'Denver City', lat: 32.9648, lng: -102.8307, hub: 'Lubbock', zone: 5 },
  { name: 'Dimmitt', lat: 34.5498, lng: -102.3093, hub: 'Lubbock', zone: 5 },
  { name: 'Tulia', lat: 34.5237, lng: -101.7579, hub: 'Lubbock', zone: 5 },
  // Lubbock Zone 6
  { name: 'Andrews', lat: 32.3187, lng: -102.5457, hub: 'Lubbock', zone: 6 },
  { name: 'Friona', lat: 34.6390, lng: -102.7240, hub: 'Lubbock', zone: 6 },
  { name: 'Bovina', lat: 34.5120, lng: -102.8885, hub: 'Lubbock', zone: 6 },
  { name: 'Farwell', lat: 34.3834, lng: -103.0365, hub: 'Lubbock', zone: 6 },
  // Childress Zone 1
  { name: 'Childress', lat: 34.4267, lng: -100.2040, hub: 'Childress', zone: 1 },
  { name: 'Estelline', lat: 34.5495, lng: -100.4329, hub: 'Childress', zone: 1 },
  // Childress Zone 2
  { name: 'Quanah', lat: 34.2973, lng: -99.7451, hub: 'Childress', zone: 2 },
  { name: 'Wellington', lat: 34.8572, lng: -100.2151, hub: 'Childress', zone: 2 },
  { name: 'Memphis', lat: 34.7273, lng: -100.5379, hub: 'Childress', zone: 2 },
  { name: 'Paducah', lat: 34.0131, lng: -100.3093, hub: 'Childress', zone: 2 },
  // Childress Zone 3
  { name: 'Vernon', lat: 34.1542, lng: -99.2951, hub: 'Childress', zone: 3 },
  { name: 'Clarendon', lat: 34.9373, lng: -100.8890, hub: 'Childress', zone: 3 },
  { name: 'Shamrock', lat: 35.2179, lng: -100.2465, hub: 'Childress', zone: 3 },
  { name: 'Crowell', lat: 33.9831, lng: -99.7248, hub: 'Childress', zone: 3 },
  { name: 'Turkey', lat: 34.3984, lng: -100.8968, hub: 'Childress', zone: 3 },
  { name: 'Chillicothe', lat: 34.2573, lng: -99.5151, hub: 'Childress', zone: 3 },
  { name: 'Quitaque', lat: 34.3623, lng: -101.0468, hub: 'Childress', zone: 3 },
  { name: 'Matador', lat: 34.0151, lng: -100.8204, hub: 'Childress', zone: 3 },
  // Childress Zone 4
  { name: 'Silverton', lat: 34.4717, lng: -101.3046, hub: 'Childress', zone: 4 },
  { name: 'Benjamin', lat: 33.5834, lng: -99.7948, hub: 'Childress', zone: 4 },
  { name: 'Guthrie', lat: 33.6176, lng: -100.3284, hub: 'Childress', zone: 4 },
  { name: 'Wheeler', lat: 35.4487, lng: -100.2704, hub: 'Childress', zone: 4 },
  // Childress Zone 5
  { name: 'Munday', lat: 33.4495, lng: -99.6248, hub: 'Childress', zone: 5 },
  { name: 'Goree', lat: 33.4737, lng: -99.5270, hub: 'Childress', zone: 5 },
  { name: 'Briscoe', lat: 35.6445, lng: -100.1729, hub: 'Childress', zone: 5 },
];

function getCityRate(city) {
  const radii = city.hub === 'Lubbock' ? LUBBOCK_ZONE_RADII : CHILDRESS_ZONE_RADII;
  return radii.find((r) => r.zone === city.zone) || { routine: '—', rush: '—' };
}

function haversineMi(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/* Fetch true driving distance (miles) from `{lat,lng}` to both hubs using
   Google Distance Matrix. Falls back to Haversine if the API errors out so
   the calculator never strands the user. */
function getDrivingMilesToHubs(lat, lng) {
  return new Promise((resolve) => {
    if (!(window.google && window.google.maps && window.google.maps.DistanceMatrixService)) {
      resolve({
        dLub: haversineMi(LUBBOCK_HUB.lat, LUBBOCK_HUB.lng, lat, lng),
        dChi: haversineMi(CHILDRESS_HUB.lat, CHILDRESS_HUB.lng, lat, lng),
        source: 'haversine',
      });
      return;
    }
    const svc = new window.google.maps.DistanceMatrixService();
    svc.getDistanceMatrix(
      {
        origins: [{ lat, lng }],
        destinations: [
          { lat: LUBBOCK_HUB.lat, lng: LUBBOCK_HUB.lng },
          { lat: CHILDRESS_HUB.lat, lng: CHILDRESS_HUB.lng },
        ],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      },
      (response, status) => {
        if (status !== 'OK' || !response || !response.rows || !response.rows[0]) {
          resolve({
            dLub: haversineMi(LUBBOCK_HUB.lat, LUBBOCK_HUB.lng, lat, lng),
            dChi: haversineMi(CHILDRESS_HUB.lat, CHILDRESS_HUB.lng, lat, lng),
            source: 'haversine',
          });
          return;
        }
        const elements = response.rows[0].elements;
        const valLub = elements[0]?.status === 'OK' ? elements[0].distance.value : null;
        const valChi = elements[1]?.status === 'OK' ? elements[1].distance.value : null;
        // distance.value is in meters → convert to statute miles
        const M2MI = 0.000621371;
        resolve({
          dLub: valLub != null ? valLub * M2MI : haversineMi(LUBBOCK_HUB.lat, LUBBOCK_HUB.lng, lat, lng),
          dChi: valChi != null ? valChi * M2MI : haversineMi(CHILDRESS_HUB.lat, CHILDRESS_HUB.lng, lat, lng),
          source: valLub != null && valChi != null ? 'driving' : 'mixed',
        });
      }
    );
  });
}

function computeZone({ lat, lng, state, dLub, dChi }) {
  if (state === 'NM') {
    return {
      kind: 'nm',
      title: 'New Mexico — Statewide Flat Rate',
      routine: '$125',
      rush: '$175',
      note: 'Flat-rate coverage across all 33 NM counties.',
    };
  }
  // Allow callers to pass pre-computed driving distances; otherwise fall back to Haversine.
  if (dLub == null) dLub = haversineMi(LUBBOCK_HUB.lat, LUBBOCK_HUB.lng, lat, lng);
  if (dChi == null) dChi = haversineMi(CHILDRESS_HUB.lat, CHILDRESS_HUB.lng, lat, lng);

  // --- Mileage-based zone (primary) ---
  // Zone is determined purely by true driving distance from the relevant hub
  // (Lubbock measured from 4414 82nd St; Childress from 307 Ave H SE).
  const lubMatch = LUBBOCK_ZONE_RADII.find((z) => dLub <= z.max + ZONE_GRACE_MI);
  const chiMatch = CHILDRESS_ZONE_RADII.find((z) => dChi <= z.max + ZONE_GRACE_MI);

  const candidates = [];
  if (lubMatch) candidates.push({ hub: 'Lubbock', distance: Math.round(dLub), ...lubMatch });
  if (chiMatch) candidates.push({ hub: 'Childress', distance: Math.round(dChi), ...chiMatch });

  // --- Covered-city override (ONLY when out of normal mileage range) ---
  // A few explicitly-listed covered towns sit BEYOND the furthest zone cap but are
  // still serviced at a set zone (e.g., Andrews, TX — ~106 mi from Lubbock, served
  // as Zone 6). This override applies only when no mileage zone matched, so in-range
  // addresses (e.g., Petersburg ~39 mi) are ALWAYS priced by true mileage.
  if (candidates.length === 0) {
    let nearestCity = null;
    let nearestCityDist = Infinity;
    for (const city of COVERED_CITIES) {
      const d = haversineMi(city.lat, city.lng, lat, lng);
      if (d < nearestCityDist) {
        nearestCityDist = d;
        nearestCity = city;
      }
    }
    if (nearestCity && nearestCityDist <= 8) {
      const rate = getCityRate(nearestCity);
      const hubDist = nearestCity.hub === 'Lubbock' ? Math.round(dLub) : Math.round(dChi);
      return {
        kind: 'hub',
        title: `${nearestCity.hub} Hub — Zone ${nearestCity.zone}`,
        routine: rate.routine,
        rush: rate.rush,
        note: `Covered city: ${nearestCity.name}. Approximately ${hubDist} miles from our ${nearestCity.hub} hub.`,
        hub: nearestCity.hub,
        zone: nearestCity.zone,
        distance: hubDist,
        distLubbock: Math.round(dLub),
        distChildress: Math.round(dChi),
      };
    }

    if (state === 'TX') {
      return {
        kind: 'out_of_tx_hub',
        title: 'Out of Hub Range — Network Partner',
        routine: '$90 – $175',
        rush: 'Location-dependent',
        note: 'Texas address beyond WesTex hub zones — served via certified network partner. 3–5 business days standard.',
        distance: Math.round(Math.min(dLub, dChi)),
        distLubbock: Math.round(dLub),
        distChildress: Math.round(dChi),
      };
    }
    return {
      kind: 'network',
      title: 'Nationwide Network Service',
      routine: '$90 – $175',
      rush: 'Location-dependent',
      note: 'Served via our vetted nationwide network of certified process servers. 3–5 business days standard.',
    };
  }

  candidates.sort((a, b) => {
    const pa = parseInt(a.routine.replace(/\D/g, ''), 10);
    const pb = parseInt(b.routine.replace(/\D/g, ''), 10);
    if (pa !== pb) return pa - pb;
    return a.distance - b.distance;
  });
  const pick = candidates[0];
  return {
    kind: 'hub',
    title: `${pick.hub} Hub — Zone ${pick.zone}`,
    routine: pick.routine,
    rush: pick.rush,
    note: `Approximately ${pick.distance} miles from our ${pick.hub} hub.`,
    hub: pick.hub,
    zone: pick.zone,
    distance: pick.distance,
    // Rationale: show how far each hub is so customers see WHY this hub was picked
    distLubbock: Math.round(dLub),
    distChildress: Math.round(dChi),
  };
}

const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#0a0a0b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0a0a0b' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6b6b6f' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#1a1a1c' }] },
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#2a2a2e' }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#242428' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#101012' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1e1e22' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2a2a30' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#050507' }] },
];

const MAP_INITIAL_CENTER = { lat: 34.0, lng: -103.7 };
const MAP_INITIAL_ZOOM = 6;

function CoverageCalculatorSection() {
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const infoWindowRef = useRef(null);
  const routeRef = useRef(null);
  const antTimerRef = useRef(null);
  const [result, setResult] = useState(null);
  const [query, setQuery] = useState('');
  const [mapReady, setMapReady] = useState(false);

  // Clear any existing animated route trail + its animation timer.
  const clearRoute = () => {
    if (antTimerRef.current) { clearInterval(antTimerRef.current); antTimerRef.current = null; }
    if (routeRef.current) { routeRef.current.setMap(null); routeRef.current = null; }
  };

  // Draw a subtle animated "marching ants" trail from the serving hub to the
  // searched point. Uses the real road path from the Directions API; if that's
  // unavailable, falls back to a straight geodesic line so the effect still shows.
  const drawAntTrail = (m, origin, dest) => {
    clearRoute();
    const animate = (path) => {
      const dash = {
        path: 'M 0,-1 0,1',
        strokeOpacity: 0.9,
        strokeColor: '#ed1c24',
        scale: 2.2,
      };
      const poly = new window.google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#ed1c24',
        strokeOpacity: 0.12,
        strokeWeight: 1.5,
        icons: [{ icon: dash, offset: '0', repeat: '34px' }],
        zIndex: 9000,
        map: m,
      });
      routeRef.current = poly;
      let count = 0;
      antTimerRef.current = setInterval(() => {
        count = (count + 0.5) % 100;
        const icons = poly.get('icons');
        icons[0].offset = count + '%';
        poly.set('icons', icons);
      }, 70);
      // Frame the whole route as tightly as possible while keeping BOTH the hub
      // and the searched point in view. Always seed the bounds with the two
      // endpoints (so it never collapses), then extend with the road path. A
      // delayed re-fit guards against fitBounds running before the map is sized.
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(new window.google.maps.LatLng(origin.lat, origin.lng));
      bounds.extend(new window.google.maps.LatLng(dest.lat, dest.lng));
      path.forEach((p) => bounds.extend(p));
      const padding = { top: 70, right: 70, bottom: 70, left: 70 };
      window.google.maps.event.addListenerOnce(m, 'bounds_changed', () => {
        if (m.getZoom() > 14) m.setZoom(14);
      });
      m.fitBounds(bounds, padding);
      setTimeout(() => { if (routeRef.current === poly) m.fitBounds(bounds, padding); }, 200);
    };
    const straight = () => animate([
      new window.google.maps.LatLng(origin.lat, origin.lng),
      new window.google.maps.LatLng(dest.lat, dest.lng),
    ]);
    try {
      const ds = new window.google.maps.DirectionsService();
      ds.route(
        { origin, destination: dest, travelMode: window.google.maps.TravelMode.DRIVING },
        (resp, status) => {
          if (status === 'OK' && resp.routes && resp.routes[0]) {
            animate(resp.routes[0].overview_path);
          } else {
            straight();
          }
        }
      );
    } catch (e) {
      straight();
    }
  };

  const resetMapView = () => {
    const m = mapInstanceRef.current;
    if (!m) return;
    clearRoute();
    if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
    if (infoWindowRef.current) { infoWindowRef.current.close(); }
    m.panTo(MAP_INITIAL_CENTER);
    m.setZoom(MAP_INITIAL_ZOOM);
  };

  // Stop the ant-trail animation when the component unmounts.
  useEffect(() => () => clearRoute(), []);

  // Initialize map with subtle zone circles + covered-city pinpoints
  useEffect(() => {
    let interval;
    const init = () => {
      if (!mapRef.current || mapInstanceRef.current) return;
      const m = new window.google.maps.Map(mapRef.current, {
        center: MAP_INITIAL_CENTER,
        zoom: MAP_INITIAL_ZOOM,
        disableDefaultUI: true,
        zoomControl: true,
        styles: DARK_MAP_STYLES,
        backgroundColor: '#0a0a0b',
      });
      mapInstanceRef.current = m;

      // Draw the two hub center markers (no concentric zone rings — pinpoints tell the story)
      const drawHub = (hub) => {
        new window.google.maps.Marker({
          map: m,
          position: { lat: hub.lat, lng: hub.lng },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 4.5,
            fillColor: '#ed1c24',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 1.5,
          },
          title: `${hub.name} Hub`,
          zIndex: 999,
        });
      };
      drawHub(LUBBOCK_HUB);
      drawHub(CHILDRESS_HUB);

      // Shared info window (styled via CSS overrides)
      const infoWindow = new window.google.maps.InfoWindow({
        pixelOffset: new window.google.maps.Size(0, -6),
      });
      infoWindowRef.current = infoWindow;

      // Drop a pinpoint for each covered city, colored by zone
      COVERED_CITIES.forEach((city) => {
        const color = ZONE_SHADES[city.zone - 1] || '#dc2626';
        const rate = getCityRate(city);
        const marker = new window.google.maps.Marker({
          map: m,
          position: { lat: city.lat, lng: city.lng },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 3.5,
            fillColor: color,
            fillOpacity: 0.95,
            strokeColor: '#0a0a0b',
            strokeWeight: 1,
          },
          title: `${city.name} — ${city.hub} Zone ${city.zone}`,
        });
        const html = `
          <div class="v2-iw">
            <div class="v2-iw-tag">${city.hub.toUpperCase()} HUB · ZONE ${city.zone}</div>
            <div class="v2-iw-name">${city.name}</div>
            <div class="v2-iw-rates">
              <div class="v2-iw-rate">
                <div class="v2-iw-rate-label">Routine</div>
                <div class="v2-iw-rate-val">${rate.routine}</div>
              </div>
              <div class="v2-iw-rate">
                <div class="v2-iw-rate-label">Rush</div>
                <div class="v2-iw-rate-val">${rate.rush}</div>
              </div>
            </div>
          </div>
        `;
        marker.addListener('click', () => {
          infoWindow.setContent(html);
          infoWindow.open(m, marker);
        });
      });

      // Drop a consistent-color pinpoint for every New Mexico county — entire
      // state covered at one flat rate.
      NM_COUNTIES.forEach((county) => {
        const marker = new window.google.maps.Marker({
          map: m,
          position: { lat: county.lat, lng: county.lng },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 3.5,
            fillColor: NM_MARKER_COLOR,
            fillOpacity: 0.95,
            strokeColor: '#0a0a0b',
            strokeWeight: 1,
          },
          title: `${county.name} County, NM — Statewide Flat Rate`,
        });
        const html = `
          <div class="v2-iw">
            <div class="v2-iw-tag">NEW MEXICO · STATEWIDE FLAT RATE</div>
            <div class="v2-iw-name">${county.name} County</div>
            <div class="v2-iw-rates">
              <div class="v2-iw-rate">
                <div class="v2-iw-rate-label">Routine</div>
                <div class="v2-iw-rate-val">${NM_FLAT.routine}</div>
              </div>
              <div class="v2-iw-rate">
                <div class="v2-iw-rate-label">Rush</div>
                <div class="v2-iw-rate-val">${NM_FLAT.rush}</div>
              </div>
            </div>
          </div>
        `;
        marker.addListener('click', () => {
          infoWindow.setContent(html);
          infoWindow.open(m, marker);
        });
      });
      setMapReady(true);
    };
    if (window.google && window.google.maps) {
      init();
    } else {
      interval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(interval);
          init();
        }
      }, 200);
    }
    return () => { if (interval) clearInterval(interval); };
  }, []);

  // Initialize Places Autocomplete — biased to Lubbock for local relevance
  useEffect(() => {
    let interval;
    const init = () => {
      if (!inputRef.current || !window.google.maps.places) return;
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'us' },
        fields: ['geometry', 'formatted_address', 'address_components', 'name'],
      });
      // Strong bias toward Lubbock area (Lubbock-centered ~200mi box)
      autocomplete.setBounds(
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(31.8, -103.5),
          new window.google.maps.LatLng(35.2, -100.0)
        )
      );
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) return;
        handleSelection(place);
      });
    };
    if (window.google && window.google.maps && window.google.maps.places) {
      init();
    } else {
      interval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(interval);
          init();
        }
      }, 200);
    }
    return () => { if (interval) clearInterval(interval); };
  }, []);

  const handleSelection = async (place) => {
    const loc = place.geometry.location;
    const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
    const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
    const comps = place.address_components || [];
    const stateComp = comps.find((c) =>
      c.types.includes('administrative_area_level_1')
    );
    const state = stateComp ? stateComp.short_name : null;
    // County (administrative_area_level_2) — Google returns e.g. "Lubbock County";
    // strip the trailing " County" so we can render it consistently.
    const countyComp = comps.find((c) => c.types.includes('administrative_area_level_2'));
    const county = countyComp ? countyComp.long_name.replace(/\s+County$/i, '') : null;
    // A specific service address has a street number; a bare city / town / ZIP
    // does not. Rural addresses can fall in a higher zone than the town center,
    // so we nudge the user to enter the full address for an exact quote.
    const hasStreet = comps.some((c) => c.types.includes('street_number'));

    setQuery(place.formatted_address || place.name || '');

    // While Google's Distance Matrix replies, show a tiny loading pill so users
    // see the calculator is doing real driving-mile math, not just guessing.
    setResult({ kind: 'loading', title: 'Calculating driving distance…' });

    let dLub, dChi;
    if (state !== 'NM') {
      const dist = await getDrivingMilesToHubs(lat, lng);
      dLub = dist.dLub;
      dChi = dist.dChi;
    }
    const res = computeZone({ lat, lng, state, dLub, dChi });
    // Weave the county into the address text itself (before ", USA") instead of
    // showing it as a separate tag, e.g. "…TX 79424, Lubbock County, USA".
    let formattedWithCounty = place.formatted_address || place.name || '';
    if (county) {
      const countyStr = `${county} County`;
      if (!formattedWithCounty.includes(countyStr)) {
        formattedWithCounty = /,\s*USA\s*$/.test(formattedWithCounty)
          ? formattedWithCounty.replace(/,\s*USA\s*$/, `, ${countyStr}, USA`)
          : `${formattedWithCounty}, ${countyStr}`;
      }
    }
    setResult({ ...res, formatted: formattedWithCounty, lat, lng, state, county, townOnly: !hasStreet });

    // Anonymous demand logging (fire-and-forget) — never blocks the UI. Records the
    // searched address + outcome so the admin can see where demand is coming from.
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      axios.post(`${base}/api/coverage/search-log`, {
        query: query || null,
        formatted: formattedWithCounty || null,
        lat, lng, state,
        kind: res.kind,
        hub: res.hub || null,
        zone: res.zone != null ? res.zone : null,
        routine: res.routine || null,
        rush: res.rush || null,
        distance: res.distance != null ? res.distance : null,
        town_only: !hasStreet,
        county: county || null,
      }).catch(() => {});
    } catch (e) { /* ignore */ }

    const m = mapInstanceRef.current;
    if (m) {
      clearRoute();
      if (markerRef.current) markerRef.current.setMap(null);
      if (infoWindowRef.current) infoWindowRef.current.close();
      markerRef.current = new window.google.maps.Marker({
        map: m,
        position: { lat, lng },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 4,
          fillColor: '#ffffff',
          fillOpacity: 1,
          strokeColor: '#ed1c24',
          strokeWeight: 1.5,
        },
        title: place.formatted_address || place.name || '',
        zIndex: 10000,
      });

      // When the result is served from a hub, trace an animated road trail from
      // that hub to the searched point (fitBounds frames the whole route). For
      // NM / nationwide-network results there's no hub trail, so just pan/zoom.
      if (res.kind === 'hub' && res.hub) {
        const origin = res.hub === 'Childress' ? CHILDRESS_HUB : LUBBOCK_HUB;
        drawAntTrail(m, origin, { lat, lng });
      } else {
        m.panTo({ lat, lng });
        const targetZoom = res.kind === 'nm' || res.kind === 'network' ? 8 : 13;
        m.setZoom(targetZoom);
      }
    }
  };

  const handleManualGeocode = (e) => {
    if (e.key !== 'Enter') return;
    const val = e.target.value.trim();
    if (!val || !window.google || !window.google.maps) return;
    e.preventDefault();
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: val, componentRestrictions: { country: 'US' } }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        handleSelection(results[0]);
      } else {
        setResult({
          kind: 'not_found',
          title: 'No match found',
          note: `Couldn't locate "${val}". Try a more specific city, ZIP, or address — or call (806) 401-1749.`,
        });
      }
    });
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (v.trim() === '') {
      setResult(null);
      resetMapView();
    }
  };

  const resultBadgeColor = (kind) => {
    if (kind === 'hub') return 'v2-calc-badge-hub';
    if (kind === 'nm') return 'v2-calc-badge-nm';
    if (kind === 'network' || kind === 'out_of_tx_hub') return 'v2-calc-badge-net';
    return 'v2-calc-badge-none';
  };

  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">FIND YOUR ZONE</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Type an address. <em>Get an answer.</em>
            </h2>
            <p>
              Full street address, city, or ZIP — we'll geolocate it, match it to the nearest hub, and tell you the exact zone and price. If it's outside our driving radius, we'll route it through our nationwide network.
            </p>

            <div className="v2-calc-wrap">
              <div className="v2-calc-field">
                <label className="v2-calc-label" htmlFor="v2-calc-input">Address, city, or ZIP</label>
                <input
                  id="v2-calc-input"
                  ref={inputRef}
                  type="text"
                  className="v2-calc-input"
                  placeholder="e.g. 4414 82nd St  ·  Ralls, TX  ·  79424"
                  value={query}
                  onChange={handleInputChange}
                  onKeyDown={handleManualGeocode}
                  data-testid="v2-calc-input"
                  autoComplete="off"
                />
                <div className="v2-calc-hint">Start typing for address suggestions — or press Enter to search a city / ZIP</div>
              </div>

              {result && (
                <div className={`v2-calc-result ${resultBadgeColor(result.kind)}`} data-testid="v2-calc-result">
                  <div className="v2-calc-result-head">
                    <div className="v2-calc-result-tag">{result.kind === 'not_found' ? 'NOT FOUND' : 'RESULT'}</div>
                    <div className="v2-calc-result-title">{result.title}</div>
                    {result.formatted && <div className="v2-calc-result-addr" data-testid="v2-calc-addr">{result.formatted}</div>}
                    {result.kind === 'hub' && result.distance != null && (
                      <div className="v2-calc-result-miles" data-testid="v2-calc-miles">
                        ≈ {result.distance} mi from our {result.hub} hub
                      </div>
                    )}
                  </div>
                  {result.kind !== 'not_found' && result.kind !== 'loading' && (
                    <div className="v2-calc-result-rates">
                      <div className="v2-calc-rate">
                        <div className="v2-calc-rate-label">Routine</div>
                        <div className="v2-calc-rate-val">{result.routine}</div>
                      </div>
                      <div className="v2-calc-rate">
                        <div className="v2-calc-rate-label">Rush</div>
                        <div className="v2-calc-rate-val">{result.rush}</div>
                      </div>
                    </div>
                  )}
                  {result.townOnly && result.kind !== 'not_found' && result.kind !== 'loading' && result.kind !== 'nm' && (
                    <p className="v2-calc-result-nudge" data-testid="v2-calc-nudge">
                      Enter the full address — rural addresses may price into a higher zone than the town center.
                    </p>
                  )}
                  {result.kind !== 'hub' && result.kind !== 'not_found' && result.kind !== 'loading' && result.note && (
                    <p className="v2-calc-result-note">{result.note}</p>
                  )}
                </div>
              )}

              <div className="v2-calc-map-wrap">
                <div className="v2-calc-map-legend">
                  <span className="v2-calc-legend-item"><span className="v2-calc-legend-dot v2-calc-dot-hub" /> Hub</span>
                  <span className="v2-calc-legend-item"><span className="v2-calc-legend-dot v2-calc-dot-pin" /> Your location</span>
                  <span className="v2-calc-legend-item"><span className="v2-calc-legend-dot v2-calc-dot-city" /> Covered cities — click any dot for pricing</span>
                  <span className="v2-calc-legend-item"><span className="v2-calc-legend-dot v2-calc-dot-nm" /> New Mexico counties — statewide flat rate</span>
                </div>
                <div
                  ref={mapRef}
                  className="v2-calc-map"
                  data-testid="v2-calc-map"
                  aria-label="Coverage map showing Lubbock and Childress hub zones"
                />
                {!mapReady && <div className="v2-calc-map-loading">Loading map…</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


export function V2AreasPricing() {
  return (
    <V2Layout>
      <SEO
        title="Service Areas & Pricing — Texas & New Mexico Process Servers | WesTex Legal"
        description="See coverage zones and transparent pricing for process serving across West Texas, the Texas Panhandle, and all of New Mexico. Get an instant quote by entering an address."
        path="/areas-pricing"
        image={`${SITE_URL}/lubbock-coverage.jpg`}
        schema={buildBreadcrumb([
          { name: 'Home', path: '/' },
          { name: 'Areas & Pricing', path: '/areas-pricing' },
        ])}
      />
      <V2CoverageHero />
      <HeroDivider />
      <CoverageOverviewSection />
      <NeonDivider delay={0.5} />
      <CoverageCalculatorSection />
      <NeonDivider delay={1.0} />
      <LubbockZonesSection />
      <NeonDivider delay={1.5} />
      <ChildressZonesSection />
      <NeonDivider delay={2.0} />
      <NewMexicoSection />
      <NeonDivider delay={2.5} />
      <NetworkSection />
    </V2Layout>
  );
}

/* ============== V2 NOTARY — fully custom editorial ============== */
function V2NotaryHero() {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.2}px, 0) scale(${1 + Math.min(y, 600) * 0.0002})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  const scrollToBook = (e) => {
    e.preventDefault();
    const el = document.getElementById('v2-notary-book');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="v2-about-hero" data-testid="v2-notary-hero">
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: 'url(/lubbock-notary.jpg)' }}
      />
      <div className="v2-hero-grain" />
      <div className="v2-about-hero-body">
        <div className="v2-mono-tag">NOTARY PUBLIC · LUBBOCK, TX</div>
        <h1 className="v2-about-title">
          We come to you.<br />
          <span className="v2-about-title-line2"><em>Seal it in ink.</em></span>
        </h1>
        <p className="v2-about-sub">
          A commissioned notary public in Lubbock, TX — mobile Lubbock notary services across Lubbock County and West Texas. Affidavits, acknowledgments, oaths, loan signings, and mortgage packages, wherever you are.
        </p>
        <div className="v2-notary-hero-cta">
          <a href="#v2-notary-book" onClick={scrollToBook} className="v2-btn v2-btn-primary" data-testid="v2-notary-hero-book">
            <span>Book an appointment</span>
            <span className="v2-btn-arrow">→</span>
          </a>
          <a href="tel:8064011749" className="v2-btn v2-btn-ghost" data-testid="v2-notary-hero-call">
            <span>(806) 401-1749</span>
            <span className="v2-btn-arrow">→</span>
          </a>
        </div>
      </div>
    </section>
  );
}

const NOTARY_STATS = [
  { num: '10am\u20138pm', label: 'Daily hours', small: true },
  { num: '7', label: 'Days a week' },
  { num: 'Mobile', label: 'We come to you' },
  { num: '18+', label: 'Years in the system' },
];

function NotaryOverviewSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">THE SERVICE</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              A notary public in Lubbock, <em>at your door.</em>
            </h2>
            <p>
              Mobile notary public and Lubbock notary services for Lubbock County and the surrounding West Texas communities. Available <em style={{color:'hsl(357, 90%, 62%)', fontStyle:'normal', fontWeight:600}}>10am – 8pm, every day</em> — routine weekday, weekend, holiday, and full mortgage/loan signings.
            </p>
            <p>
              Every notarization is logged, documented, and performed to Texas state standards. We bring the journal, the seal, and the patience for anything from a single affidavit to a full refinance package.
            </p>
            <div className="v2-cov-stats">
              {NOTARY_STATS.map((s) => (
                <div key={s.label} className="v2-cov-stat">
                  <div className={`v2-cov-stat-num${s.small ? ' is-sm' : ''}`}>{s.num}</div>
                  <div className="v2-cov-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const ACCEPTABLE_ID = [
  'State-issued Driver\u2019s License',
  'State-issued ID Card',
  'US Passport',
  'Military ID',
];

const ID_REQUIREMENTS = [
  'Current and not expired',
  'Must have a clear photo',
  'Must be the original document',
  'Photocopies are not acceptable',
];

function NotaryWhatToBringSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">WHAT TO BRING</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              ID in hand. <em>That’s it.</em>
            </h2>
            <p>
              To notarize any document in Texas, the signer must present current, government-issued photo ID. The name on the ID must match the name on the document being notarized.
            </p>
            <div className="v2-notary-bring-grid">
              <div className="v2-notary-bring-col">
                <div className="v2-notary-bring-label">Acceptable identification</div>
                <ul className="v2-notary-bring-list">
                  {ACCEPTABLE_ID.map((t) => (
                    <li key={t}><span className="v2-notary-bring-tick">✓</span>{t}</li>
                  ))}
                </ul>
              </div>
              <div className="v2-notary-bring-col">
                <div className="v2-notary-bring-label">Requirements</div>
                <ul className="v2-notary-bring-list">
                  {ID_REQUIREMENTS.map((t) => (
                    <li key={t}><span className="v2-notary-bring-tick">✓</span>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="v2-about-pullquote">
              <span className="v2-about-pullquote-mark">"</span>
              If the names don’t match, <em>it doesn’t get notarized.</em> No exceptions.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const NOTARY_PRICING = [
  {
    num: '01',
    title: 'Weekday Notary',
    desc: 'Monday through Friday, standard business hours.',
    rows: [
      { label: 'Routine', price: '$75', note: 'Scheduled 4+ hours in advance' },
      { label: 'Rush', price: '$150', note: 'Scheduled 1+ hour in advance' },
    ],
  },
  {
    num: '02',
    title: 'Weekend Notary',
    desc: 'Saturday and Sunday coverage for time-sensitive signings.',
    rows: [
      { label: 'Routine', price: '$115', note: 'Scheduled 4+ hours in advance' },
      { label: 'Rush', price: '$230', note: 'Scheduled 1+ hour in advance' },
    ],
  },
  {
    num: '03',
    title: 'Mortgage / Loan Notary',
    desc: 'Full loan signings, refinance packages, and closing documents.',
    rows: [
      { label: 'Routine', price: '$175', note: 'Scheduled 4+ hours in advance' },
      { label: 'Rush', price: '$350', note: 'Scheduled 1+ hour in advance' },
    ],
  },
  {
    num: '04',
    title: 'Holiday Notary',
    desc: 'Available on major holidays throughout the year.',
    holidays: true,
    rows: [
      { label: 'Flat Rate', price: '$225', note: 'Subject to availability on the day' },
    ],
  },
];

/* Holiday date helpers (kept synced with /api/notary/holidays and V1 calculations) */
function v2GetHolidayDates() {
  const today = new Date();
  let year = today.getFullYear();
  if (today.getMonth() === 11 && today.getDate() === 31) year += 1;

  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const nthWeekday = (y, m, weekday, n) => {
    const first = new Date(y, m, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    return new Date(y, m, 1 + offset + (n - 1) * 7);
  };
  const lastWeekday = (y, m, weekday) => {
    const last = new Date(y, m + 1, 0);
    const offset = (last.getDay() - weekday + 7) % 7;
    return new Date(y, m + 1, 0 - offset);
  };
  const easter = (y) => {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const n = Math.floor((h + l - 7 * m + 114) / 31);
    const p = (h + l - 7 * m + 114) % 31;
    return new Date(y, n - 1, p + 1);
  };
  const goodFri = (y) => { const e = easter(y); const g = new Date(e); g.setDate(e.getDate() - 2); return g; };

  const list = [
    { name: "New Year's Day", date: new Date(year, 0, 1) },
    { name: 'Martin Luther King Jr. Day', date: nthWeekday(year, 0, 1, 3) },
    { name: "Presidents' Day", date: nthWeekday(year, 1, 1, 3) },
    { name: 'Good Friday', date: goodFri(year) },
    { name: 'Easter', date: easter(year) },
    { name: "Mother's Day", date: nthWeekday(year, 4, 0, 2) },
    { name: 'Memorial Day', date: lastWeekday(year, 4, 1) },
    { name: 'Juneteenth', date: new Date(year, 5, 19) },
    { name: "Father's Day", date: nthWeekday(year, 5, 0, 3) },
    { name: 'Independence Day', date: new Date(year, 6, 4) },
    { name: 'Labor Day', date: nthWeekday(year, 8, 1, 1) },
    { name: 'Columbus Day', date: nthWeekday(year, 9, 1, 2) },
    { name: 'Halloween', date: new Date(year, 9, 31) },
    { name: 'Veterans Day', date: new Date(year, 10, 11) },
    { name: 'Thanksgiving Day', date: nthWeekday(year, 10, 4, 4) },
    { name: 'Christmas Eve', date: new Date(year, 11, 24) },
    { name: 'Christmas Day', date: new Date(year, 11, 25) },
    { name: "New Year's Eve", date: new Date(year, 11, 31) },
  ];
  list.sort((a, b) => a.date - b.date);

  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const nextIdx = list.findIndex((h) => h.date >= todayMidnight);
  return {
    year,
    list: list.map((h) => ({ name: h.name, formatted: fmt(h.date) })),
    nextIdx: nextIdx === -1 ? null : nextIdx,
  };
}

function HolidayDropdown() {
  const [open, setOpen] = useState(false);
  const { list, nextIdx } = v2GetHolidayDates();
  const next = nextIdx !== null ? list[nextIdx] : null;
  return (
    <div className="v2-holiday-drop" data-testid="v2-holiday-drop">
      {next && (
        <div className="v2-holiday-next">
          <span className="v2-holiday-next-label">Next holiday —</span>{' '}
          <span className="v2-holiday-next-val">{next.name} · {next.formatted}</span>
        </div>
      )}
      <button
        type="button"
        className="v2-holiday-toggle"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        data-testid="v2-holiday-toggle"
      >
        <span>{open ? 'Hide observed holidays' : 'See all observed holidays'}</span>
        <span className={`v2-holiday-toggle-icon ${open ? 'is-open' : ''}`}>▾</span>
      </button>
      <div className={`v2-holiday-list-wrap ${open ? 'is-open' : ''}`}>
        <ul className="v2-holiday-list">
          {list.map((h, i) => (
            <li key={h.name} className={`v2-holiday-row ${i === nextIdx ? 'is-next' : ''}`}>
              <span className="v2-holiday-row-name">{h.name}</span>
              <span className="v2-holiday-row-dot" />
              <span className="v2-holiday-row-date">{h.formatted}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function NotaryPricingSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">PRICING</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Flat rates. <em>No hidden line items.</em>
            </h2>
            <p>
              Mileage outside our standard service radius is calculated at booking. Every appointment includes travel, seal, and journal entry.
            </p>
            <div className="v2-notary-price-list">
              {NOTARY_PRICING.map((cat) => (
                <div key={cat.num} className="v2-notary-price-block">
                  <div className="v2-notary-price-head">
                    <div className="v2-notary-price-num">{cat.num}</div>
                    <div>
                      <div className="v2-notary-price-title">{cat.title}</div>
                      <div className="v2-notary-price-desc">{cat.desc}</div>
                    </div>
                  </div>
                  <div className="v2-notary-price-rows">
                    {cat.rows.map((r) => (
                      <div key={r.label} className="v2-notary-price-row">
                        <div className="v2-notary-price-row-label">{r.label}</div>
                        <div className="v2-notary-price-row-note">{r.note}</div>
                        <div className="v2-notary-price-row-val">{r.price}</div>
                      </div>
                    ))}
                  </div>
                  {cat.holidays && <HolidayDropdown />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NotaryBookingSection() {
  return (
    <section id="v2-notary-book" className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">BOOK A NOTARY</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Pick a service. <em>Pick a time.</em>
            </h2>
            <p>
              Choose your document, pick a date and time, and we’ll calculate the price for you. Confirmation lands in your inbox the moment it’s booked.
            </p>
            <div className="v2-notary-widget-wrap" data-testid="v2-notary-widget">
              <NotaryBookingWidget />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const NOTARY_CITIES = [
  'Lubbock', 'Wolfforth', 'Shallowater', 'Slaton', 'Idalou', 'Ransom Canyon',
  'Levelland', 'Littlefield', 'Plainview', 'Brownfield', 'Post', 'Tahoka', 'Lamesa',
];

function NotaryServiceAreaSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">SERVICE AREA</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Across <em>West Texas.</em>
            </h2>
            <p>
              Mobile notary coverage spans Lubbock County and the surrounding region. Loan signings, affidavits, power of attorney, real estate packages — we bring the seal to your location.
            </p>
            <div className="v2-notary-city-list">
              {NOTARY_CITIES.map((c, i) => (
                <span key={c} className="v2-notary-city">
                  {c}{i < NOTARY_CITIES.length - 1 ? <span className="v2-zone-dot"> · </span> : null}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const NOTARY_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'NotaryPublic',
  '@id': `${SITE_URL}/notary#business`,
  name: 'WesTex Legal Mobile Notary',
  alternateName: 'WesTex Mobile Notary',
  description:
    'Texas-commissioned mobile notary serving Lubbock and surrounding West Texas 7 days a week. Loan signings, real estate closing packages, affidavits, powers of attorney, and jurats — we travel to your home, office, or hospital.',
  url: `${SITE_URL}/notary`,
  logo: `${SITE_URL}/westex-logo.png`,
  image: `${SITE_URL}/lubbock-notary.jpg`,
  telephone: '+1-806-401-1749',
  email: 'info@westexlegal.com',
  priceRange: '$75 - $350',
  currenciesAccepted: 'USD',
  paymentAccepted: ['Credit Card', 'Debit Card', 'Cash', 'Check'],
  parentOrganization: { '@type': 'LegalService', name: 'WesTex Legal', url: SITE_URL },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '4414 82nd St, Suite 212-322',
    addressLocality: 'Lubbock',
    addressRegion: 'TX',
    postalCode: '79424',
    addressCountry: 'US',
  },
  geo: { '@type': 'GeoCoordinates', latitude: '33.5779', longitude: '-101.8552' },
  areaServed: NOTARY_CITIES.map((c) => ({ '@type': 'City', name: `${c}, TX` })),
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '07:00',
      closes: '21:00',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Mobile Notary Services',
    itemListElement: [
      { '@type': 'Offer', priceCurrency: 'USD', price: '75', itemOffered: { '@type': 'Service', name: 'Weekday Mobile Notarization' } },
      { '@type': 'Offer', priceCurrency: 'USD', price: '115', itemOffered: { '@type': 'Service', name: 'Weekend Mobile Notarization' } },
      { '@type': 'Offer', priceCurrency: 'USD', price: '225', itemOffered: { '@type': 'Service', name: 'Holiday Mobile Notarization' } },
      { '@type': 'Offer', priceCurrency: 'USD', price: '175', itemOffered: { '@type': 'Service', name: 'Real Estate / Loan Signing' } },
    ],
  },
};

const NOTARY_FAQS = [
  {
    q: 'Do you offer a free notary in Lubbock?',
    a: 'We are a professional mobile notary service, so a travel-and-service fee applies (weekday notarizations start at $75). Unlike a free walk-in notary, we come to your home, office, or hospital anywhere in Lubbock and West Texas, 7 days a week — including evenings, weekends, and holidays.',
  },
  {
    q: 'Are you open on Saturdays and weekends?',
    a: 'Yes. Our Lubbock notary services are available every day, 10am–8pm, including Saturdays, Sundays, and holidays. Weekend mobile notarizations are $115 and holiday appointments are $225.',
  },
  {
    q: 'Can you notarize real estate and loan documents?',
    a: 'Absolutely. We handle full real estate closing packets, refinance and mortgage loan signings, deeds, and powers of attorney. Loan and real estate signing packages are a flat $175. Bring a current, government-issued photo ID that matches the name on the documents.',
  },
  {
    q: 'How much does a mobile notary in Lubbock cost?',
    a: 'Weekday notarizations are $75, weekends $115, holidays $225, and real estate / loan signings $175. Pricing is flat and transparent — you see the total before you book.',
  },
  {
    q: 'Which areas do you cover for notary services?',
    a: 'We serve Lubbock and the surrounding West Texas communities, including Wolfforth, Shallowater, Slaton, Idalou, Ransom Canyon, Levelland, Littlefield, Plainview, Brownfield, Post, Tahoka, and Lamesa.',
  },
];

const NOTARY_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: NOTARY_FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

function NotaryFAQSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight" data-testid="v2-notary-faq">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">NOTARY FAQ</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Common <em>questions.</em>
            </h2>
            <div className="v2-notary-faq-list">
              {NOTARY_FAQS.map((f) => (
                <div key={f.q} className="v2-notary-faq-item">
                  <h3 className="v2-notary-faq-q">{f.q}</h3>
                  <p className="v2-notary-faq-a">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function V2Notary() {
  return (
    <V2Layout>
      <SEO
        title="Notary Public in Lubbock, TX — Mobile Notary & Loan Signings | WesTex Legal"
        description="Mobile notary public in Lubbock, TX serving West Texas 7 days a week. Loan signings, real estate, affidavits, POAs. Weekday $75, weekend $115. Book your Lubbock notary online."
        path="/notary"
        image={`${SITE_URL}/lubbock-notary.jpg`}
        schema={[
          NOTARY_BUSINESS_SCHEMA,
          NOTARY_FAQ_SCHEMA,
          buildBreadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Notary Public Lubbock', path: '/notary' },
          ]),
        ]}
      />
      <V2NotaryHero />
      <HeroDivider />
      <NotaryOverviewSection />
      <NeonDivider delay={0.5} />
      <NotaryWhatToBringSection />
      <NeonDivider delay={1.0} />
      <NotaryPricingSection />
      <NeonDivider delay={1.5} />
      <NotaryBookingSection />
      <NeonDivider delay={2.0} />
      <NotaryServiceAreaSection />
      <NeonDivider delay={2.5} />
      <NotaryFAQSection />
    </V2Layout>
  );
}

/* ============== V2 FAQ — fully custom editorial ============== */
function V2FAQHero() {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.2}px, 0) scale(${1 + Math.min(y, 600) * 0.0002})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section className="v2-about-hero" data-testid="v2-faq-hero">
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: 'url(/lubbock-faq.jpg)' }}
      />
      <div className="v2-hero-grain" />
      <div className="v2-about-hero-body">
        <div className="v2-mono-tag">FREQUENTLY ASKED</div>
        <h1 className="v2-about-title">
          Answers,<br />
          <span className="v2-about-title-line2"><em>on the record.</em></span>
        </h1>
        <p className="v2-about-sub">
          Everything you might want to know before sending us papers. If it's not here, call us — we actually pick up.
        </p>
      </div>
    </section>
  );
}

const FAQ_CATEGORIES = [
  {
    num: '01',
    tag: 'PROCESS SERVING BASICS',
    heading: 'The fundamentals.',
    questions: [
      { q: 'What is process serving?', a: "Process serving is the legal procedure of delivering court documents to parties involved in a legal case. This includes summons, complaints, subpoenas, and other legal notices. Professional process servers ensure documents are delivered according to legal requirements and provide proof of service to the courts." },
      { q: "Why can't I serve documents myself?", a: 'Texas law requires process servers to be certified by the Judicial Branch Certification Commission (JBCC). Additionally, using a neutral third party ensures proper legal procedures are followed and eliminates any potential conflicts of interest that could invalidate the service.' },
      { q: 'How long does it take to serve documents?', a: 'Service times vary by zone and service type. Zone 1 (Lubbock and Childress counties) typically receives same-day service. Zones 2-5 range from next-day to 5-day service windows. Rush service is available for urgent needs with expedited delivery.' },
      { q: 'What happens if someone refuses to accept the documents?', a: "In Texas, individuals cannot legally 'refuse' service. If someone won't take the documents, we can complete service by placing the documents near the person and explaining what they are. The service is still legally valid even if they refuse to physically accept the papers." },
      { q: 'Can documents be served on weekends or holidays?', a: 'According to Texas Rule of Civil Procedure 6, civil process cannot be served on Sundays except in emergency situations like injunctions. Saturday service is generally permitted. We offer special weekend and holiday notary services for urgent needs.' },
    ],
  },
  {
    num: '02',
    tag: 'SERVICE AREAS & PRICING',
    heading: 'Where we go. What it costs.',
    questions: [
      { q: 'Which areas do you serve?', a: 'We serve from two strategic bases: Lubbock Base covers 5 zones in West Texas including Lubbock, Hale, Crosby, Lynn, Hockley counties and extended areas. Childress Base covers 4 zones in North Texas Panhandle including Childress, Hall, Collingsworth, Cottle, Hardeman counties. We also provide complete New Mexico statewide coverage at $125 routine / $175 rush.' },
      { q: 'How is pricing determined?', a: 'Pricing is zone-based starting at $75 for Zone 1 routine service. Each zone has specific pricing for routine and rush service: Zone 1 ($75/$100), Zone 2 ($100/$130), Zone 3 ($125/$160), Zone 4 ($150/$195), Zone 5 ($175/$225), Zone 6 ($200/$255). All prices are per address served.' },
      { q: 'Do you serve outside Texas and New Mexico?', a: 'Yes, through our network of certified process servers nationwide, we can serve documents in all 50 states. We also have experience with Hague Convention services for international process serving requirements. Contact us for custom quotes.' },
      { q: "What if the person has moved or can't be located?", a: 'We use advanced skip tracing techniques and comprehensive databases to locate individuals. Our skip tracing service ($35 per entity) searches current and past addresses, phone numbers, employers, relatives, and associates to find the current location.' },
    ],
  },
  {
    num: '03',
    tag: 'LEGAL REQUIREMENTS & METHODS',
    heading: 'The rules of the road.',
    questions: [
      { q: 'What methods of service are allowed in Texas?', a: 'Texas law permits several methods: personal hand delivery, substitute service at a person\'s home or workplace, service through certified mail with tracking, and in some cases, service by publication with court approval. Each method must follow specific legal guidelines.' },
      { q: 'Can someone else at the address accept service?', a: "Yes, under Texas rules, documents can be served to another competent adult at the recipient's residence or place of business if direct personal service is not possible. This is called 'substitute service' and is legally valid when properly executed." },
      { q: 'How is proof of service provided?', a: 'We provide a court-acceptable Affidavit of Service that details when, where, and how documents were served. This notarized document includes all information required by the courts and serves as legal proof that proper service was completed.' },
      { q: 'What happens if service cannot be completed?', a: 'If we cannot complete service after diligent attempts, we provide an Affidavit of Non-Service or Due Diligence affidavit detailing all attempts made. This may allow the court to approve alternative service methods like service by publication.' },
    ],
  },
  {
    num: '04',
    tag: 'CASE MANAGEMENT & TRACKING',
    heading: 'Visibility, end to end.',
    questions: [
      { q: 'How can I track my case status?', a: 'We use a comprehensive case management system that provides real-time tracking. All updates are automatically sent to your email, including attempt details, successful service confirmation, and any special circumstances encountered during service.' },
      { q: 'How many attempts are made to serve documents?', a: 'We make up to 4 attempts to serve documents for both routine and rush service. Each attempt is documented with detailed information about the time, location, circumstances, and any individuals contacted during the attempt.' },
      { q: 'Do I need to check on my case status?', a: "No, our automated system sends updates directly to your email for every attempt and successful service. You'll receive comprehensive information without needing to contact us for status updates, saving you time and ensuring transparency." },
      { q: 'What if I need to modify service instructions?', a: 'Contact us immediately if you need to change service instructions, add special handling requirements, or update address information. Our case management system allows for real-time updates to ensure accurate service.' },
    ],
  },
  {
    num: '05',
    tag: 'ADDITIONAL SERVICES & STANDARDS',
    heading: 'Beyond process service.',
    questions: [
      { q: 'Do you offer notary services?', a: "Yes, we provide mobile notary services including routine weekday service, weekend service, holiday service, and specialized mortgage/loan document notarization. Acceptable ID includes State Issued Driver's License, State Issued ID Card, US Passport, and Military ID." },
      { q: 'What is skip tracing and when is it needed?', a: 'Skip tracing is locating individuals who cannot be found at known addresses. We use multiple databases to find current addresses, phone numbers, employers, relatives, and associates. This service costs $35 per entity and is often needed when initial service attempts are unsuccessful.' },
      { q: 'Are you licensed and insured?', a: 'Yes, WesTex Legal is fully licensed by the Texas Judicial Branch Certification Commission (JBCC), bonded, and insured. We are proud members of the National Association of Professional Process Servers (NAPPS), maintaining the highest professional standards.' },
      { q: 'Do you provide background checks?', a: 'Yes, we offer comprehensive background checks including criminal records, evictions, foreclosures, bankruptcies, liens, judgments, property records, corporate filings, and professional licenses. Pricing is $65 per entity with detailed reporting.' },
      { q: 'What makes WesTex Legal different from other process servers?', a: 'We combine 18+ years of legal industry experience with advanced technology, offer real-time case tracking, maintain strategic locations for comprehensive Texas/New Mexico coverage, provide 24/7 availability, and focus on client satisfaction with professional, reliable service delivery.' },
    ],
  },
  {
    num: '06',
    tag: 'CLIENT PORTAL',
    heading: 'Your jobs, on demand.',
    questions: [
      { q: 'What is the Client Portal?', a: 'The Client Portal is a secure online platform where you can view all your service jobs, track their status in real-time, download affidavits and service documents, and manage your account. It provides 24/7 access to your case information without needing to call or email us.' },
      { q: 'Do I have to use the portal to submit jobs?', a: "No, the portal is completely optional for job submission. You can continue sending jobs via email exactly as you're used to doing. Once we set up your job in our case management system, it will automatically appear in your portal for tracking. The portal is primarily designed to give you real-time visibility into your jobs, not to replace your current workflow." },
      { q: 'How do I access the Client Portal?', a: "Visit westexlegal.com/portal to log in or register. If you're an existing client, contact us to receive your portal invitation. New clients can register directly, and once your account is approved, you'll have immediate access to all your jobs." },
      { q: 'What can I do in the Client Portal?', a: 'In the portal, you can: view all your active and completed jobs, see detailed attempt history and timeline for each service (including the specific address attempted), download Affidavits of Service and other documents, and track job status in real-time. You can also submit new jobs through the portal if you prefer, but this is entirely optional.' },
      { q: 'How do I download my Affidavit of Service?', a: "Once a job is completed, log into the Client Portal, navigate to the job details, and click the download button in the Affidavits section. You'll receive the official affidavit document instantly, available anytime you need it." },
      { q: 'Will my emailed jobs appear in the portal?', a: "Yes! When you send us a job via email and we enter it into our case management system, it automatically syncs to your Client Portal. You'll be able to track its progress, view all service attempts, and download completed affidavits — all without changing how you submit jobs." },
      { q: 'Is the Client Portal secure?', a: 'Yes, the Client Portal uses industry-standard encryption and secure authentication to protect your information. All data is transmitted securely, and your account is protected with password authentication.' },
      { q: 'Is there a referral program?', a: "Yes! Once registered, you'll receive a unique referral code. Share it with colleagues or other attorneys, and when someone uses your code to sign up and submits their first job, you'll earn a $25 credit toward your next invoice. You can find your referral code and track your earnings in the Client Portal dashboard." },
    ],
  },
];

function FAQAccordionItem({ qa, itemKey, openKey, setOpenKey }) {
  const isOpen = openKey === itemKey;
  return (
    <div className={`v2-faq-item ${isOpen ? 'is-open' : ''}`}>
      <button
        className="v2-faq-q"
        onClick={() => setOpenKey(isOpen ? null : itemKey)}
        aria-expanded={isOpen}
        data-testid={`v2-faq-q-${itemKey}`}
      >
        <span className="v2-faq-q-text">{qa.q}</span>
        <span className="v2-faq-q-icon" aria-hidden="true">{isOpen ? '–' : '+'}</span>
      </button>
      <div className="v2-faq-a-wrap" style={{ maxHeight: isOpen ? '800px' : '0px' }}>
        <div className="v2-faq-a">{qa.a}</div>
      </div>
    </div>
  );
}

function FAQCategorySection({ cat, setOpenKey, openKey }) {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">{cat.tag}</div>
          </div>
          <div className="v2-about-row-body">
            <div className="v2-faq-cat-head">
              <span className="v2-faq-cat-num">{cat.num}</span>
              <h2 className="v2-about-h2">{cat.heading}</h2>
            </div>
            <div className="v2-faq-list">
              {cat.questions.map((qa, i) => {
                const itemKey = `${cat.num}-${i}`;
                return (
                  <FAQAccordionItem
                    key={itemKey}
                    qa={qa}
                    itemKey={itemKey}
                    openKey={openKey}
                    setOpenKey={setOpenKey}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQContactSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">STILL STUCK?</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Didn't find it? <em>Just ask.</em>
            </h2>
            <p>
              Our team actually answers the phone. Call us or drop a message — we'll get you a real answer, fast.
            </p>
            <div className="v2-faq-cta-row">
              <a href="tel:8064011749" className="v2-btn v2-btn-primary" data-testid="v2-faq-call">
                <span>Call (806) 401-1749</span>
                <span className="v2-btn-arrow">→</span>
              </a>
              <a href="/contact" className="v2-btn v2-btn-ghost" data-testid="v2-faq-contact">
                <span>Send a message</span>
                <span className="v2-btn-arrow">→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function V2FAQ() {
  const [openKey, setOpenKey] = useState(null);
  return (
    <V2Layout>
      <SEO
        title="Process Serving FAQ — Texas Rules, Pricing & Timelines | WesTex Legal"
        description="Answers to common questions about process serving in Texas: legal requirements, JBCC certification, weekend service, skip tracing, pricing zones, and what to expect."
        path="/faq"
        image={`${SITE_URL}/lubbock-faq.jpg`}
        schema={buildBreadcrumb([
          { name: 'Home', path: '/' },
          { name: 'FAQ', path: '/faq' },
        ])}
      />
      <V2FAQHero />
      <HeroDivider />
      {FAQ_CATEGORIES.map((cat, i) => (
        <React.Fragment key={cat.num}>
          <FAQCategorySection cat={cat} openKey={openKey} setOpenKey={setOpenKey} />
          {i < FAQ_CATEGORIES.length - 1 && <NeonDivider delay={0.4 + i * 0.3} />}
        </React.Fragment>
      ))}
      <NeonDivider delay={2.5} />
      <FAQContactSection />
    </V2Layout>
  );
}

/* ============== V2 CONTACT — fully custom editorial ============== */
function V2ContactHero() {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.2}px, 0) scale(${1 + Math.min(y, 600) * 0.0002})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section className="v2-about-hero" data-testid="v2-contact-hero">
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: 'url(/lubbock-contact.jpg)' }}
      />
      <div className="v2-hero-grain" />
      <div className="v2-about-hero-body">
        <div className="v2-mono-tag">CONTACT</div>
        <h1 className="v2-about-title">
          Let's talk<br />
          <span className="v2-about-title-line2"><em>process.</em></span>
        </h1>
        <p className="v2-about-sub">
          Tell us about your case, ask a pricing question, or just say hey. We respond fast — usually within the same business day.
        </p>
      </div>
    </section>
  );
}

const CASE_TYPES = [
  'General Inquiry', 'Process Serving', 'Rush Service', 'Background Check',
  'Document Retrieval', 'Skip Tracing', 'Vehicle Search', 'Mobile Notary',
  'Statewide Service', 'Nationwide Service',
];

function ContactFormSection() {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
    case_type: 'General Inquiry',
  });
  const [status, setStatus] = useState({ type: 'idle', msg: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: 'loading', msg: 'Sending your message…' });
    try {
      const res = await axios.post('https://formspree.io/f/xgvlevdk', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        case_type: formData.case_type,
        _replyto: formData.email,
        _subject: `New Contact Form Submission: ${formData.subject}`,
      }, {
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });
      if (res.status === 200) {
        setStatus({ type: 'success', msg: 'Thank you. We\'ll be in touch within 24 hours.' });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '', case_type: 'General Inquiry' });
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus({ type: 'error', msg: 'Something broke. Try again, or call us directly at (806) 401-1749.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">SEND A MESSAGE</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Tell us about <em>the case.</em>
            </h2>
            <p>
              The more detail you give us up front, the faster we can quote, schedule, and deliver. Required fields are marked with an asterisk.
            </p>
            <form className="v2-contact-form" onSubmit={onSubmit} data-testid="v2-contact-form" noValidate>
              <div className="v2-contact-grid">
                <div className="v2-contact-field">
                  <label htmlFor="v2-c-name">Full Name *</label>
                  <input
                    id="v2-c-name" type="text" name="name" required
                    value={formData.name} onChange={onChange} disabled={isSubmitting}
                    data-testid="v2-contact-name"
                  />
                </div>
                <div className="v2-contact-field">
                  <label htmlFor="v2-c-email">Email *</label>
                  <input
                    id="v2-c-email" type="email" name="email" required
                    value={formData.email} onChange={onChange} disabled={isSubmitting}
                    data-testid="v2-contact-email"
                  />
                </div>
                <div className="v2-contact-field">
                  <label htmlFor="v2-c-phone">Phone</label>
                  <input
                    id="v2-c-phone" type="tel" name="phone"
                    value={formData.phone} onChange={onChange} disabled={isSubmitting}
                    data-testid="v2-contact-phone"
                  />
                </div>
                <div className="v2-contact-field">
                  <label htmlFor="v2-c-type">Service Type</label>
                  <select
                    id="v2-c-type" name="case_type"
                    value={formData.case_type} onChange={onChange} disabled={isSubmitting}
                    data-testid="v2-contact-type"
                  >
                    {CASE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="v2-contact-field">
                <label htmlFor="v2-c-subject">Subject *</label>
                <input
                  id="v2-c-subject" type="text" name="subject" required
                  value={formData.subject} onChange={onChange} disabled={isSubmitting}
                  data-testid="v2-contact-subject"
                />
              </div>
              <div className="v2-contact-field">
                <label htmlFor="v2-c-message">Message *</label>
                <textarea
                  id="v2-c-message" name="message" rows="6" required
                  placeholder="Deadlines, locations, document type, anything we should know up front…"
                  value={formData.message} onChange={onChange} disabled={isSubmitting}
                  data-testid="v2-contact-message"
                />
              </div>
              <div className="v2-contact-submit-row">
                <button
                  type="submit" disabled={isSubmitting}
                  className="v2-btn v2-btn-primary"
                  data-testid="v2-contact-submit"
                >
                  <span>{isSubmitting ? 'Sending…' : 'Send Message'}</span>
                  <span className="v2-btn-arrow">→</span>
                </button>
                {status.type !== 'idle' && (
                  <div className={`v2-contact-status v2-contact-status-${status.type}`} data-testid="v2-contact-status">
                    {status.msg}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactDirectSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">DIRECT LINES</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              Or skip the form. <em>Call us.</em>
            </h2>
            <p>
              We pick up the phone. The fastest way to get a real answer is to dial in.
            </p>
            <div className="v2-contact-direct-grid">
              <a href="tel:8064011749" className="v2-contact-direct-item" data-testid="v2-contact-phone-link">
                <div className="v2-contact-direct-label">Phone</div>
                <div className="v2-contact-direct-val">(806) 401-1749</div>
                <div className="v2-contact-direct-note">Tap to call</div>
              </a>
              <a href="mailto:WesTexLegal@gmail.com" className="v2-contact-direct-item" data-testid="v2-contact-email-link">
                <div className="v2-contact-direct-label">Email</div>
                <div className="v2-contact-direct-val">WesTexLegal@gmail.com</div>
                <div className="v2-contact-direct-note">Tap to compose</div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactHoursSection() {
  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag">HOURS &amp; COVERAGE</div>
          </div>
          <div className="v2-about-row-body">
            <h2 className="v2-about-h2">
              When we work. <em>Where we work.</em>
            </h2>
            <div className="v2-contact-hours-list">
              <div className="v2-contact-hours-row">
                <div className="v2-contact-hours-label">Process Serving</div>
                <div className="v2-contact-hours-val">24 / 7 — emergency &amp; rush available</div>
              </div>
              <div className="v2-contact-hours-row">
                <div className="v2-contact-hours-label">Mobile Notary</div>
                <div className="v2-contact-hours-val">10am – 8pm, every day</div>
              </div>
            </div>
            <div className="v2-about-pullquote">
              <span className="v2-about-pullquote-mark">"</span>
              Texas (West &amp; Panhandle) · New Mexico (Statewide) · <em>Nationwide network on standby.</em>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function V2Contact() {
  return (
    <V2Layout>
      <SEO
        title="Contact WesTex Legal — Call (806) 401-1749 | Lubbock, TX"
        description="Contact WesTex Legal for process serving, mobile notary, and skip tracing. Call (806) 401-1749, email WesTexLegal@gmail.com, or send a message — 24/7 response."
        path="/contact"
        image={`${SITE_URL}/lubbock-contact.jpg`}
        schema={buildBreadcrumb([
          { name: 'Home', path: '/' },
          { name: 'Contact', path: '/contact' },
        ])}
      />
      <V2ContactHero />
      <HeroDivider />
      <ContactFormSection />
      <NeonDivider delay={0.5} />
      <ContactDirectSection />
      <NeonDivider delay={1.0} />
      <ContactHoursSection />
    </V2Layout>
  );
}


/* ============== V2 404 — editorial not-found page ============== */
function V2NotFoundHero() {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.2}px, 0)`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section className="v2-about-hero" data-testid="v2-notfound-hero" style={{ minHeight: '70vh' }}>
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: 'url(/lubbock-skyline.jpg)' }}
      />
      <div className="v2-hero-grain" />
      <div className="v2-about-hero-body">
        <div className="v2-mono-tag">ERROR · 404 · NO RECORD</div>
        <h1 className="v2-about-title">
          Nothing filed <em>on this docket.</em>
          <br />
          <span className="v2-about-title-line2">The page you're after <em>doesn't exist.</em></span>
        </h1>
        <p className="v2-about-sub">
          Either it moved, got served to the wrong address, or never made it past intake. Let's get you back on the right path.
        </p>
      </div>
    </section>
  );
}

function V2NotFoundLinks() {
  const links = [
    { to: '/', label: 'Home', num: '01', blurb: 'Back to the front page.' },
    { to: '/services', label: 'Services', num: '02', blurb: 'What we actually do.' },
    { to: '/areas-pricing', label: 'Coverage & Pricing', num: '03', blurb: 'Where we go. What it costs.' },
    { to: '/notary', label: 'Mobile Notary', num: '04', blurb: 'We come to you. Seal it in ink.' },
    { to: '/job-request', label: 'Submit a Job', num: '05', blurb: 'Get something served today.' },
    { to: '/contact', label: 'Contact', num: '06', blurb: 'Call, text, email.' },
  ];
  return (
    <section className="v2-notfound-links" data-testid="v2-notfound-links" style={{ padding: '80px 24px 120px', background: '#0a0a0a', color: '#fff' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="v2-mono-tag" style={{ marginBottom: 20 }}>TRY ONE OF THESE</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 500, margin: '0 0 48px', letterSpacing: '-0.02em' }}>
          Common destinations <em style={{ color: 'hsl(357, 90%, 62%)' }}>from here.</em>
        </h2>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
          {links.map((l) => (
            <li key={l.to} style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
              <Link
                to={l.to}
                data-testid={`v2-notfound-link-${l.num}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr auto',
                  alignItems: 'center',
                  gap: 24,
                  padding: '28px 0',
                  color: '#fff',
                  textDecoration: 'none',
                  transition: 'background 0.3s ease, padding 0.3s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.paddingLeft = '16px'; e.currentTarget.style.background = 'rgba(220, 38, 38, 0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.paddingLeft = '0'; e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ color: 'hsl(357, 90%, 62%)', fontFamily: 'ui-monospace, monospace', fontSize: 12, letterSpacing: '0.1em' }}>{l.num}</span>
                <div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(20px, 2.4vw, 32px)', fontWeight: 500, letterSpacing: '-0.01em' }}>{l.label}</div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, marginTop: 4 }}>{l.blurb}</div>
                </div>
                <span style={{ color: 'hsl(357, 90%, 62%)', fontSize: 24 }}>→</span>
              </Link>
            </li>
          ))}
        </ul>
        <div style={{ marginTop: 48, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <a href="tel:8064011749" className="v2-btn v2-btn-primary" data-testid="v2-notfound-call">
            <span>Call (806) 401-1749</span>
            <span className="v2-btn-arrow">→</span>
          </a>
          <Link to="/contact" className="v2-btn v2-btn-ghost" data-testid="v2-notfound-contact">
            <span>Email Us</span>
            <span className="v2-btn-arrow">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

export function V2NotFound() {
  // Ensure HTTP 404 semantics for search engines / crawlers that execute JS
  useEffect(() => {
    try {
      let meta = document.querySelector('meta[name="prerender-status-code"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'prerender-status-code');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', '404');
    } catch (e) { /* noop */ }
    return () => {
      try {
        const m = document.querySelector('meta[name="prerender-status-code"]');
        if (m) m.remove();
      } catch (e) { /* noop */ }
    };
  }, []);

  return (
    <V2Layout>
      <SEO
        title="404 — Page Not Found | WesTex Legal"
        description="The page you're looking for doesn't exist. Browse our process serving and mobile notary services or contact us at (806) 401-1749."
        path="/404"
        noindex
      />
      <V2NotFoundHero />
      <HeroDivider />
      <V2NotFoundLinks />
    </V2Layout>
  );
}
